import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Briefcase, 
  Edit, 
  Save, 
  X, 
  Plus, 
  Trash2, 
  Lock, 
  Loader2,
  AlertTriangle 
} from "lucide-react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@/lib/schema";
import { getJobSettingsLegacy, updateJobSettings, checkOptionsInUse, JobSettingsData } from "@/api/jobSettings";
import { 
  EMPLOYMENT_TYPES, 
  JOB_CATEGORIES, 
  JOB_PRIORITY_OPTIONS 
} from "@/lib/jobFormUtils";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

interface OptionSection {
  id: 'employmentTypes' | 'categories' | 'priorities';
  title: string;
  description: string;
  icon: typeof Briefcase;
  color: string;
}

export default function JobSettings() {
  const { userData, loading: authLoading } = useAuth();
  const { toast } = useToast();

  // State
  const [settings, setSettings] = useState<JobSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editData, setEditData] = useState<{ [key: string]: string[] }>({});
  const [newOption, setNewOption] = useState('');
  const [optionsInUse, setOptionsInUse] = useState<{ [key: string]: boolean }>({});

  // Check if user is super admin
  const isSuperAdmin = userData?.role === UserRole.SUPER_ADMIN;

  const sections: OptionSection[] = [
    {
      id: 'employmentTypes',
      title: 'Employment Types',
      description: 'Available employment type options for job postings',
      icon: Briefcase,
      color: 'blue'
    },
    {
      id: 'categories',
      title: 'Job Categories',
      description: 'Available category options for job classification',
      icon: Briefcase,
      color: 'green'
    },
    {
      id: 'priorities',
      title: 'Priority Levels',
      description: 'Available priority options for job urgency',
      icon: Briefcase,
      color: 'orange'
    }
  ];

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getJobSettingsLegacy();
      
      if (data) {
        setSettings(data);
      } else {
        // Initialize with default values from jobFormUtils (sorted alphabetically)
        const defaultSettings: JobSettingsData = {
          employmentTypes: EMPLOYMENT_TYPES.map(t => t.value).sort(),
          categories: JOB_CATEGORIES.map(c => c.value).sort(),
          priorities: JOB_PRIORITY_OPTIONS.map(p => p.value).sort(),
          skills: [],
          locations: ['Remote'],
          defaultRewardAmount: 1000,
          minRewardAmount: 500,
          maxRewardAmount: 10000,
          referralExpirationDays: 90,
          lastUpdated: new Date(),
          updatedBy: ''
        };
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to load job settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = async (sectionId: string) => {
    if (!settings) return;
    
    try {
      // Check which options are in use
      const field = sectionId === 'employmentTypes' ? 'employmentType' : 
                   sectionId === 'categories' ? 'category' : 'priority';
      const options = settings[sectionId as keyof JobSettingsData] as string[];
      const inUse = await checkOptionsInUse(field, options);
      
      setOptionsInUse(inUse);
      setEditingSection(sectionId);
      setEditData({ [sectionId]: [...options].sort() });
      setNewOption('');
    } catch (error) {
      console.error('Error checking options in use:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to check option usage",
        variant: "destructive"
      });
    }
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditData({});
    setNewOption('');
    setOptionsInUse({});
  };

  const addOption = () => {
    if (!newOption.trim() || !editingSection) return;
    
    const currentOptions = editData[editingSection] || [];
    if (currentOptions.includes(newOption.trim())) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Duplicate Option",
        description: "This option already exists",
        variant: "destructive"
      });
      return;
    }
    
    setEditData(prev => ({
      ...prev,
      [editingSection]: [...currentOptions, newOption.trim()].sort()
    }));
    setNewOption('');
  };

  const removeOption = (sectionId: string, optionToRemove: string) => {
    if (optionsInUse[optionToRemove]) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Cannot Remove",
        description: "This option is currently used by existing jobs",
        variant: "destructive"
      });
      return;
    }
    
    setEditData(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).filter(option => option !== optionToRemove).sort()
    }));
  };

  const saveSettings = async () => {
    if (!settings || !userData || !editingSection) return;
    
    try {
      setSaving(true);
      
      const updatedSettings = {
        ...settings,
        [editingSection]: editData[editingSection]
      };
      delete (updatedSettings as any).lastUpdated;
      delete (updatedSettings as any).updatedBy;
      
      await updateJobSettings(updatedSettings, userData.email || `${userData.firstName} ${userData.lastName}` || 'Unknown');
      
      setSettings({
        ...updatedSettings,
        lastUpdated: new Date(),
        updatedBy: userData.email || `${userData.firstName} ${userData.lastName}` || 'Unknown'
      });
      
      cancelEditing();
      
      toast({
        title: "Settings Saved",
        description: "Job settings have been updated successfully"
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Show loading or access denied
  if (authLoading || loading) {
    return (
      <SuperAdminLayout title="Job Settings" subtitle="Manage job posting options and configurations">
        <LoadingScreen text="Loading..." />
      </SuperAdminLayout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <SuperAdminLayout title="Job Settings" subtitle="Manage job posting options and configurations">
        <Alert className="max-w-md mx-auto mt-20">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Access denied. Only super administrators can view this page.
          </AlertDescription>
        </Alert>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout 
      title="Job Settings" 
      subtitle="Manage job posting options and configurations"
    >
      <div className="space-y-6">
        {/* Last Updated Info */}
        {settings?.lastUpdated && (
          <div className="text-sm text-gray-500 space-grotesk">
            Last updated: {settings.lastUpdated.toLocaleDateString()} by {settings.updatedBy}
          </div>
        )}

        {/* Settings Sections */}
        <div className="space-y-6">
          {sections.map((section) => {
            const isEditing = editingSection === section.id;
            const options = isEditing 
              ? editData[section.id] || []
              : (settings?.[section.id] as string[]) || [];

            return (
              <Card key={section.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <section.icon className={`h-5 w-5 text-${section.color}-600`} />
                        {section.title}
                      </CardTitle>
                      <CardDescription>
                        {section.description}
                      </CardDescription>
                    </div>
                    {!isEditing && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => startEditing(section.id)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <div className="space-y-4">
                      {/* Add new option */}
                      <div className="flex items-center gap-2">
                        <Input
                          value={newOption}
                          onChange={(e) => setNewOption(e.target.value)}
                          placeholder="Add new option..."
                          onKeyDown={(e) => e.key === 'Enter' && addOption()}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={addOption}
                          disabled={!newOption.trim()}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Options list */}
                      <div className="space-y-2">
                        {options.map((option) => (
                          <div
                            key={option}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <span className="space-grotesk text-sm">{option}</span>
                            <div className="flex items-center gap-2">
                              {optionsInUse[option] && (
                                <Badge variant="secondary" className="space-grotesk text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  In Use
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeOption(section.id, option)}
                                disabled={optionsInUse[option]}
                                className="h-6 w-6 p-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          variant="primary"
                          onClick={saveSettings}
                          disabled={saving}
                          size="sm"
                          className="space-grotesk"
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4 mr-2" />
                          )}
                          Save Changes
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={cancelEditing}
                          disabled={saving}
                          size="sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>

                      {/* Warning for options in use */}
                      {Object.values(optionsInUse).some(inUse => inUse) && (
                        <Alert>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            Options marked as "In Use" cannot be removed as they are currently 
                            being used by existing jobs.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {options.map((option) => (
                        <Badge
                          key={option}
                          variant="secondary"
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
